import React from 'react';
import './DuplicateDetectionModal.css';

interface DuplicateDetectionModalProps {
  isOpen: boolean;
  duplicateFiles: string[];
  onClose: () => void;
  onRetry?: () => void;
}

const DuplicateDetectionModal: React.FC<DuplicateDetectionModalProps> = ({
  isOpen,
  duplicateFiles,
  onClose,
  onRetry
}) => {
  if (!isOpen) return null;

  const isSingleFile = duplicateFiles.length === 1;

  return (
    <div className="duplicate-modal-overlay" onClick={onClose}>
      <div className="duplicate-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="duplicate-modal-header">
          <div className="duplicate-modal-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                stroke="#F59E0B" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="duplicate-modal-title">
            <h3>Duplicate {isSingleFile ? 'File' : 'Files'} Detected</h3>
            <p className="duplicate-modal-subtitle">
              {isSingleFile 
                ? 'This file has already been uploaded' 
                : `${duplicateFiles.length} files have already been uploaded`
              }
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="duplicate-modal-content">
          <div className="duplicate-files-list">
            {duplicateFiles.map((fileName, index) => (
              <div key={index} className="duplicate-file-item">
                <div className="duplicate-file-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" 
                      stroke="#6B7280" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="duplicate-file-name">{fileName}</span>
                <div className="duplicate-file-status">
                  <span className="duplicate-status-badge">Already uploaded</span>
                </div>
              </div>
            ))}
          </div>

          <div className="duplicate-modal-message">
            <p>
              To proceed, you can either:
            </p>
            <ul className="duplicate-options-list">
              <li>
                <strong>Rename the {isSingleFile ? 'file' : 'files'}</strong> and try uploading again
              </li>
              <li>
                <strong>Remove the existing document{isSingleFile ? '' : 's'}</strong> from the case first
              </li>
              <li>
                <strong>Skip the duplicate{isSingleFile ? '' : 's'}</strong> if you don't need them
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="duplicate-modal-footer">
          <button 
            className="duplicate-btn-secondary"
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Cancel Upload
          </button>
          
          {onRetry && (
            <button 
              className="duplicate-btn-primary"
              onClick={onRetry}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 11L10 13L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Try Different Files
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateDetectionModal; 